import { Avatar, Box, Button, Center, Stack, useDisclosure } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import gql from 'graphql-tag';
import _ from 'lodash';
import { forwardRef, Ref, useCallback, useLayoutEffect, useMemo } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import { MenuBackdrop, MenuItem } from '~components/MenuBackdrop';
import { getInitials } from '~components/ui/styles/Avatar';
import { useAnalyticsReporter } from '~utils/analytics';
import { useDimensions } from '~utils/dimensions';
import { getElevatedOrganizationId } from '~utils/elevatedOrganization';
import { useFeatureFlag } from '~utils/features';
import { useLocalStorage } from '~utils/localStorage';
import { BuildingIcon, ChevronDownIcon, SearchIcon } from '../ui/icons';
import { CustomerSwitcher_OrganizationFragment } from './__generated__/CustomerSwitcher.graphql';

interface Props {
  activeCustomerHandle?: string;
  organization: CustomerSwitcher_OrganizationFragment;
}

export function CustomerSwitcher({ activeCustomerHandle, organization }: Props) {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [ref, { width }, measure] = useDimensions<HTMLDivElement>();
  const location = useLocation();
  const { t } = useTranslation();
  const { verifyUserPermissions } = useAuth();
  const { isEnabled: isAdminServiceabilityEnabled } = useFeatureFlag('adminServiceability');
  const canUseAdminSearch =
    isAdminServiceabilityEnabled && verifyUserPermissions([Permission.ServiceAdminGlobalSearch]);
  const isAdminSearchActive = location.pathname.includes('/admin/search');
  const isElevated = Boolean(getElevatedOrganizationId());
  // Only Service Admins work across organizations, so only they need to see which one this is.
  const organizationCaption = isElevated
    ? organization.name
    : canUseAdminSearch
    ? t('myOrganizationCaption', { name: organization.name })
    : undefined;
  useLayoutEffect(() => {
    measure();
  }, [isOpen, measure]);

  const activeCustomer = useMemo(
    () => organization.customers.find((c) => c.handle === activeCustomerHandle),
    [organization.customers, activeCustomerHandle],
  );

  const { recentlyUsed } = useRecentlyUsedCustomers();
  const customersByRecentlyUsedAndLimited = useMemo(() => {
    return _.chain(organization.customers)
      .sortBy((customer) =>
        recentlyUsed.indexOf(customer.handle) !== -1
          ? recentlyUsed.indexOf(customer.handle)
          : organization.customers.length,
      )
      .filter((c) => c.id !== activeCustomer?.id)
      .take(3)
      .value();
  }, [activeCustomer, organization.customers, recentlyUsed]);

  const analytics = useAnalyticsReporter();

  const handleCustomerClicked = () => {
    analytics.track('headerCustomerSelected');
    onClose();
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Box position="relative" display="flex" alignItems="center">
        <MenuBackdrop width={width + 70} isOpen={isOpen}>
          <MenuItem
            to={`/${organization.handle}/customers`}
            onClick={onClose}
            icon={<BuildingIcon width="6" />}
            iconWidth="40px"
            caption={organizationCaption}
          >
            {t('allCustomers')}
          </MenuItem>
          {canUseAdminSearch && (
            <MenuItem
              to={`/${organization.handle}/admin/search`}
              onClick={onClose}
              icon={<SearchIcon width="6" />}
              iconWidth="40px"
              caption={t('admin')}
            >
              {t('search')}
            </MenuItem>
          )}
          <Box marginTop="5" paddingX="2" color="gray.400" fontSize="14px" fontWeight="semibold">
            {t('recentCustomers')}
          </Box>
          <Stack direction="column" spacing="2">
            {customersByRecentlyUsedAndLimited.map((customer) => (
              <MenuItem
                key={customer.id}
                to={`/${organization.handle}/customers/${customer.handle}`}
                onClick={handleCustomerClicked}
                iconWidth="40px"
                icon={
                  <Avatar
                    name={customer.name}
                    getInitials={getInitials}
                    src={customer.avatarUrl ?? undefined}
                    variant="square"
                    size="md"
                    width="40px"
                    height="40px"
                    _groupHover={{
                      background: 'blue.50',
                    }}
                  />
                }
              >
                {customer.name}
              </MenuItem>
            ))}
          </Stack>
        </MenuBackdrop>
        <Trigger
          caption={
            isAdminSearchActive ? t('admin') : activeCustomer ? t('customer') : organizationCaption
          }
          icon={isAdminSearchActive ? <SearchIcon width="6" /> : undefined}
          avatar={activeCustomer?.avatarUrl ?? organization.avatarUrl ?? undefined}
          ref={ref}
          isOpen={isOpen}
          onToggle={onToggle}
          initialsName={activeCustomer?.name ?? organization?.name}
          label={
            isAdminSearchActive
              ? t('search')
              : activeCustomer?.name ??
                (location.pathname.includes('/settings/') ? t('settings') : t('allCustomers'))
          }
        />
      </Box>
    </ClickAwayListener>
  );
}

CustomerSwitcher.graphql = {
  fragments: {
    CustomerSwitcher_organization: gql`
      fragment CustomerSwitcher_organization on Organization {
        handle
        name
        customers {
          id
          ...CustomerSwitcher_customer
        }
        avatarUrl
      }
    `,
    CustomerSwitcher_customer: gql`
      fragment CustomerSwitcher_customer on Customer {
        id
        name
        handle
        avatarUrl
        waveSubscription {
          id
        }
        displayCount
        ...CustomerSubscriptionData_customer
      }
    `,
  },
};

interface TriggerProps {
  caption?: string;
  icon?: JSX.Element;
  label: string;
  initialsName: string;
  avatar: string | undefined;
  isOpen: boolean;
  onToggle: () => void;
}

const Trigger = forwardRef(function Trigger(
  { avatar, caption, icon, isOpen, onToggle, label, initialsName }: TriggerProps,
  ref: Ref<HTMLDivElement>,
) {
  return (
    <Box ref={ref} zIndex="sticky">
      <Button
        aria-haspopup={true}
        aria-expanded={isOpen}
        zIndex={0}
        variant="unstyled"
        justifyContent="flex-start"
        height="100%"
        textAlign="inherit"
        paddingX="2"
        onClick={onToggle}
      >
        <Box role="group" display="flex" alignItems="center" flexWrap="nowrap">
          <Box>
            {icon ? (
              <Center width="12" height="12" borderRadius="md" bgColor="gray.50">
                {icon}
              </Center>
            ) : (
              <Avatar
                variant="square"
                size="md"
                name={initialsName}
                getInitials={getInitials}
                src={avatar}
              />
            )}
          </Box>
          <Box
            flex="1"
            marginLeft="4"
            display={{
              base: 'none',
              lg: 'block',
            }}
          >
            {caption && (
              <Box color="gray.400" fontWeight="semibold" fontSize="xs">
                {caption}
              </Box>
            )}
            <Box
              display="flex"
              flex="1"
              flexWrap="nowrap"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                color={isOpen ? 'blue.500' : 'gray.900'}
                fontWeight="semibold"
                fontSize="md"
                paddingRight="9"
                transition="width 0.3s"
                _groupHover={{
                  color: 'blue.500',
                }}
              >
                {label}
              </Box>

              <Box marginLeft="2">
                <ChevronDownIcon
                  color={isOpen ? 'blue.500' : 'gray.900'}
                  transition="transform 0.2s"
                  transform={isOpen ? 'rotate(180deg)' : undefined}
                  transformOrigin="center"
                  _groupHover={{
                    color: 'blue.500',
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Button>
    </Box>
  );
});

export function useRecentlyUsedCustomers() {
  const maxRecentlyUsed = 10;
  const [recentlyUsed, setRecentlyUsed] = useLocalStorage<string[]>('recentCustomers', []);

  const addToRecentlyUsed = useCallback(
    (handle: string) => {
      setRecentlyUsed((r) => _.take(_.uniq([handle, ...r]), maxRecentlyUsed));
    },
    [setRecentlyUsed],
  );

  return { recentlyUsed, addToRecentlyUsed };
}
